import React from 'react';
import {
  Box,
  Card,
  CardContent,
  CardMedia,
  Chip,
  Typography,
  Button,
} from '@mui/material';
import {
  ShoppingCart as ShoppingCartIcon,
  ImageNotSupported as ImageNotSupportedIcon,
} from '@mui/icons-material';
import { formatPrice } from '../../Formatter/formatPrice';
import { OrderItem } from '../../types/order';

interface MenuListProps {
  items: OrderItem[];
  onAddClick: (item: OrderItem) => void;
  cloudFrontUrl: string;
  /**
   * オーダー画面テンプレート（店舗設定・BE ADR 0068 / feast-fe ADR 0033）。
   * IMAGE（既定）= 画像つきカードのグリッド / TEXT = 画像なしのテキスト行リスト。
   */
  template?: 'IMAGE' | 'TEXT';
}

export const MenuList: React.FC<MenuListProps> = ({
  items,
  onAddClick,
  cloudFrontUrl,
  template = 'IMAGE',
}) => {
  // 受注停止（売り切れ）中はメニューを表示したままカート追加を不可にする（FE ADR 0054）
  const handleAddClick = (item: OrderItem) => {
    if (item.isSuspended) return;
    onAddClick(item);
  };

  if (template === 'TEXT') {
    // 画像を用意しない店舗向け: 品名・価格・追加ボタンのシンプルな行リスト。
    return (
      <Card sx={{ mt: 2, mb: 2 }}>
        {items.filter((item) => item.isActive !== false).map((item, index) => (
          <Box
            key={item.id}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              px: 2,
              py: 1.5,
              borderTop: index === 0 ? 'none' : '1px solid',
              borderColor: 'divider',
              opacity: item.isSuspended ? 0.55 : 1,
            }}
          >
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                <Typography sx={{ fontWeight: 'bold', fontSize: '1rem' }}>{item.name}</Typography>
                {item.isSuspended && (
                  <Chip label="売り切れ" size="small" color="error" />
                )}
              </Box>
              <Typography variant="body2" color="text.secondary">
                ¥{item.price.toLocaleString()}（税抜）
              </Typography>
            </Box>
            <Button
              variant="contained"
              size="small"
              onClick={() => handleAddClick(item)}
              startIcon={item.isSuspended ? undefined : <ShoppingCartIcon />}
              disabled={item.isSuspended}
              sx={{ flexShrink: 0 }}
            >
              {item.isSuspended ? '売り切れ' : '追加'}
            </Button>
          </Box>
        ))}
      </Card>
    );
  }
  return (
    <Box
      sx={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: 2,
        mt: 2,
        mb: 2,
      }}
    >
      {items.filter((item) => item.isActive !== false).map((item) => (
        <Box
          key={item.id}
          sx={{
            flexGrow: 0,
            flexShrink: 0,
            width: {
              xs: 'calc(50% - 8px)',
              md: 'calc(33.333% - 16px)',
            },
            minWidth: 0,
          }}
        >
          <Card
            sx={{
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              opacity: item.isSuspended ? 0.55 : 1,
            }}
          >
            <Box sx={{ position: 'relative' }}>
              {item.imageUrl ? (
                <CardMedia
                  component="img"
                  sx={{
                    height: 160,
                    objectFit: 'cover',
                  }}
                  image={`${cloudFrontUrl}${item.imageUrl}`}
                  alt={item.name}
                />
              ) : (
                <Box
                  sx={{
                    height: 160,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: 'grey.100',
                  }}
                >
                  <ImageNotSupportedIcon
                    sx={{
                      fontSize: 48,
                      color: 'grey.400',
                    }}
                  />
                </Box>
              )}
              {item.isSuspended && (
                <Chip
                  label="売り切れ"
                  size="small"
                  sx={{
                    position: 'absolute',
                    top: 8,
                    left: 8,
                    bgcolor: 'rgba(0, 0, 0, 0.65)',
                    color: 'white',
                    fontWeight: 'bold',
                  }}
                />
              )}
            </Box>
            <CardContent
              sx={{
                flexGrow: 1,
                display: 'flex',
                flexDirection: 'column',
                gap: 1,
                p: 2,
              }}
            >
              <Typography
                variant="h6"
                component="h3"
                sx={{
                  fontSize: '1rem',
                  fontWeight: 'bold',
                  mb: 1,
                }}
              >
                {item.name}
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{
                  mb: 'auto',
                }}
              >
                ¥{item.price.toLocaleString()}（税抜）
              </Typography>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'flex-end',
                  mt: 2,
                }}
              >
                <Button
                  variant="contained"
                  size="small"
                  onClick={() => handleAddClick(item)}
                  startIcon={item.isSuspended ? undefined : <ShoppingCartIcon />}
                  disabled={item.isSuspended}
                >
                  {item.isSuspended ? '売り切れ' : 'カートに追加'}
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Box>
      ))}
    </Box>
  );
};
